import {
  HelpCircle,
  Clock,
  Shuffle,
  AlertTriangle,
  BookOpen,
  LifeBuoy,
  FileText,
  ListChecks,
  Network,
  LayoutGrid,
  Image,
} from "lucide-react";

export const CHECKOUT_URL = "https://spine-en.impultienda.ar/checkout/";

export const images = {
  mockup: "https://cdn.magicpatterns.com/uploads/mvTuakYpTEEJ1RKoT6wFSV/SPINE-EN.png",
  author: "https://cdn.magicpatterns.com/uploads/eo4SHEpVBYe1id9pakFi1q/RYAN_CARTER_-_SPINE.png",
};

export const beforeItems = [
  "You aren't always sure where to begin your evaluation.",
  "You have lots of information but no simple workflow.",
  "You spend too much time reviewing notes before certain sessions.",
  "You struggle to organize your clinical findings.",
  "You sometimes improvise the structure of your sessions.",
  "You worry about overlooking something important.",
  "Every patient seems to require you to reinvent your process.",
];

export const afterItems = [
  "You have a structured evaluation framework.",
  "You know what information to organize.",
  "You can use visual maps to structure your reasoning.",
  "You have checklists you can refer to.",
  "You can prepare sessions using a consistent framework.",
  "You can organize reassessment and follow-up.",
  "You can build your own professional reference system.",
];

export const painPoints = [
  {
    icon: HelpCircle,
    text: "Maybe you've studied the condition before, but you still don't have a simple process you can follow.",
  },
  {
    icon: Clock,
    text: "Maybe you spend too much time searching through books, notes, videos, and saved posts before a session.",
  },
  {
    icon: BookOpen,
    text: "Maybe you understand the theory, but translating it into a practical workflow is where things become complicated.",
  },
  {
    icon: Shuffle,
    text: "Maybe your evaluation process changes depending on the patient because you don't have a consistent framework.",
  },
  {
    icon: AlertTriangle,
    text: "Maybe you're constantly worried that you've overlooked something important.",
  },
  {
    icon: LifeBuoy,
    text: "And maybe you've wished someone would simply give you a clear system you could follow and adapt to each patient.",
  },
];

export const volumes = [
  {
    number: "I",
    title: "The Safe Spine Evaluation System™",
    body: [
      "I'll walk you through a structured approach for organizing your initial evaluation of a patient with a herniated disc.",
      "You'll learn how to organize relevant patient information, symptoms, history, examination findings, and key considerations before moving forward.",
    ],
    highlight: "The goal isn't to replace your clinical judgment. It's to give you a clearer structure for using it.",
    list: [] as string[],
  },
  {
    number: "II",
    title: "The Clinical Reasoning Map™",
    body: [
      "This is where we take all the information you've collected and turn it into a visual reasoning framework. Instead of scattered notes and isolated findings, you'll have a system for organizing:",
    ],
    highlight: "",
    list: [
      "Patient information",
      "Key findings",
      "Clinical priorities",
      "Working hypotheses",
      "Additional information",
      "Next steps",
      "Reassessment",
    ],
  },
  {
    number: "III",
    title: "The Session Planning System™",
    body: [
      "One of the biggest frustrations I hear from physical therapists is: “I know what the patient needs, but I still need to figure out how to organize the session.”",
      "This volume gives you a framework for turning assessment information and patient goals into a more organized session-planning process.",
    ],
    highlight: "",
    list: [] as string[],
  },
  {
    number: "IV",
    title: "The Progression & Reassessment System™",
    body: [
      "Patient care doesn't stop after the first session. I'll show you how to structure your follow-up process around:",
    ],
    highlight: "Always adapting the process to the individual patient.",
    list: [
      "Reassessment",
      "Progress tracking",
      "Functional changes",
      "Patient response",
      "Progression criteria",
      "Documentation",
      "Next-step planning",
    ],
  },
  {
    number: "V",
    title: "The Clinical Case Playbook™",
    body: [
      "This is where you stop simply reading and start thinking through cases. You'll work through educational clinical scenarios designed to help you practice organizing information and applying the framework.",
      "You'll see different presentations, different findings, different challenges, and different reasoning pathways.",
    ],
    highlight: "",
    list: [] as string[],
  },
  {
    number: "VI",
    title: "Build Your Personal Safe Spine Protocol™",
    body: [
      "This is where everything comes together. You'll take the frameworks, maps, checklists, and reasoning tools and create your own personal professional reference system.",
    ],
    highlight: "Something you can keep digitally. Something you can print. Something you can return to whenever you need it.",
    list: [] as string[],
  },
];

export const tools = [
  {
    icon: FileText,
    title: "30+ Practical Protocols & Clinical Tools",
    text: "Practical resources to help you organize evaluation, planning, follow-up, and reassessment.",
  },
  {
    icon: ListChecks,
    title: "Professional Checklists",
    text: "Because sometimes the simplest checklist is more useful than another 50 pages of theory. Structured checklists you can use throughout your workflow.",
  },
  {
    icon: Network,
    title: "Clinical Reasoning Maps",
    text: "Visual frameworks designed to help you organize your findings and think through the case more systematically.",
  },
  {
    icon: LayoutGrid,
    title: "Evaluation & Progress Matrices",
    text: "Simple visual structures to document relevant information, goals, follow-up, and patient progress.",
  },
  {
    icon: Image,
    title: "Quick-Reference Visual Guides",
    text: "Instead of searching through an entire manual, concise visual resources designed for fast consultation.",
  },
];

export const bonuses = [
  {
    number: 1,
    title: "30 Herniated Disc Clinical Cases™",
    value: 37,
    image: "https://cdn.magicpatterns.com/uploads/tVSFdacanfHsteZRcfG8eq/BONUS_1_SPINE-EN.png",
    text: "30 educational cases so you can practice organizing patient information and applying the Safe Spine Method™ framework across different presentations and reasoning scenarios.",
    tagline: "Don't just read about clinical reasoning. Practice it.",
    chips: [] as string[],
  },
  {
    number: 2,
    title: "The First Evaluation Checklist™",
    value: 27,
    image: "https://cdn.magicpatterns.com/uploads/bwx3YmJiHr5iBkvskuDWvz/BONUS_2_SPINE-EN.png",
    text: "A quick-reference resource designed to help you structure your initial evaluation. Instead of asking yourself “What am I forgetting?”, you have a structured reference in front of you.",
    tagline: "",
    chips: [
      "Patient information",
      "History",
      "Symptoms",
      "Physical examination",
      "Neurological screening",
      "Red flags",
      "Documentation",
      "Next steps",
    ],
  },
  {
    number: 3,
    title: "The Clinical Evaluation Mistakes Library™",
    value: 29,
    image: "https://cdn.magicpatterns.com/uploads/sKqP6azFjevbmnPVKaAfhZ/BONUS_3_SPINE-EN.png",
    text: "Common evaluation and workflow mistakes that can create confusion during the clinical process — so you can recognize potential problems before they become part of your routine.",
    tagline: "",
    chips: ["Evaluation", "Clinical reasoning", "Treatment planning", "Follow-up", "Documentation", "Communication"],
  },
  {
    number: 4,
    title: "50 Patient Communication Scripts™",
    value: 27,
    image: "https://cdn.magicpatterns.com/uploads/1wjtLaQB8C8qxaLzEaTcQi/BONUS_4_SPINE-EN.png",
    text: "Sometimes the hardest part isn't knowing what you're thinking — it's knowing how to explain it to the patient. Ready-to-adapt communication frameworks for every stage.",
    tagline: "",
    chips: [
      "Explaining the condition",
      "Setting expectations",
      "Discussing goals",
      "Addressing concerns",
      "Encouraging adherence",
      "Discussing progress",
      "Handling setbacks",
      "Next steps",
    ],
  },
  {
    number: 5,
    title: "20 Professional Clinical Checklists™",
    value: 27,
    image: "https://cdn.magicpatterns.com/uploads/edzJ3FqNSRcDkMnmW7XBjP/BONUS_5_SPINE-EN.png",
    text: "A complete collection of practical checklists designed to become part of your everyday professional workflow.",
    tagline: "",
    chips: [
      "Initial evaluation",
      "Session preparation",
      "Follow-up",
      "Reassessment",
      "Documentation",
      "Progress tracking",
      "Patient education",
      "Quick reference",
    ],
  },
];

export const testimonials = [
  {
    name: "Emily Harper, PT, DPT",
    role: "Outpatient Orthopedics · Denver, CO",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/21f9621f-1375-4bff-a6f4-68ca51056baf.jpg",
    quote:
      "I finally stopped re-reading my old notes before every disc patient. The evaluation framework and the reasoning map gave me a sequence I actually follow now.",
  },
  {
    name: "Marcus Bennett, PT",
    role: "Sports & Spine Clinic · Atlanta, GA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/d5b2dd41-a0d3-464c-a332-797210bf3be2.jpg",
    quote:
      "The session planning volume is the one I keep open. It turned what used to be improvisation into a consistent structure I can adapt to each patient.",
  },
  {
    name: "Sofia Martinez, DPT",
    role: "New Graduate · San Diego, CA",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/c892c9bb-a5b1-4ae9-ab5f-cce0089ede92.jpg",
    quote:
      "As a new grad, the checklists and the 30 cases were exactly what I needed. I feel far more organized walking into an initial evaluation.",
  },
  {
    name: "Daniel Brooks, PT, OCS",
    role: "Private Practice Owner · Austin, TX",
    photo: "https://cdn.magicpatterns.com/patterns/generated-images/61fdf580-c252-4af2-a528-ccd530fe4a9c.jpg",
    quote:
      "For $27 it's an easy decision. I printed the reassessment matrices for my whole team and our documentation is much more consistent.",
  },
];

export const faqs = [
  {
    q: "How will I receive the Safe Spine Method™?",
    a: "Immediately after your purchase, you'll receive instructions to access and download the six volumes and five bonuses digitally.",
  },
  {
    q: "Who is this for?",
    a: "The Safe Spine Method™ was created for physical therapists and appropriately licensed professionals working within their professional scope, particularly those who work with patients presenting with herniated disc and related musculoskeletal complaints.",
  },
  {
    q: "Is this a replacement for professional education?",
    a: "No. This is an educational and organizational resource. It does not replace academic education, professional guidelines, clinical judgment, or individualized patient assessment.",
  },
  {
    q: "Can I use it from my phone?",
    a: "Yes. The materials are designed for digital access and can also be printed where appropriate.",
  },
  {
    q: "Are the materials downloadable?",
    a: "Yes. You'll receive the materials digitally.",
  },
  {
    q: "Is this a one-time payment?",
    a: "Yes. You pay $27 once. There are no monthly payments.",
  },
  {
    q: "Do I get lifetime access?",
    a: "Yes. Your purchase includes lifetime access, subject to the availability of the delivery platform.",
  },
  {
    q: "What if I don't think it's right for me?",
    a: "You have a 7-day guarantee to review the materials and request a refund according to the purchase terms.",
  },
];

export const finalIncludes = [
  "6 Premium Volumes",
  "5 Professional Bonuses",
  "30+ Practical Tools",
  "Clinical Cases",
  "Evaluation Checklists",
  "Reasoning Maps",
  "Session Planning Resources",
  "Follow-Up & Reassessment Tools",
];

export const recentBuyers = [
  { name: "María González", country: "Mexico" },
  { name: "Carlos Ramírez", country: "Colombia" },
  { name: "Lucía Fernández", country: "Spain" },
  { name: "Javier Torres", country: "Argentina" },
  { name: "Valentina Rojas", country: "Chile" },
  { name: "Andrés Castillo", country: "Peru" },
  { name: "Camila Herrera", country: "Ecuador" },
  { name: "Diego Morales", country: "Guatemala" },
  { name: "Isabella Vargas", country: "Costa Rica" },
  { name: "Mateo Pérez", country: "Uruguay" },
  { name: "Daniela Suárez", country: "Dominican Republic" },
  { name: "Sebastián Núñez", country: "Panama" },
  { name: "Gabriela Méndez", country: "Venezuela" },
  { name: "Alejandro Ruiz", country: "Puerto Rico" },
];
